import {
  collection,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  query,
  where,
  Timestamp
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { User } from '../types/auth';

/**
 * UserService - Handles user-related operations
 *
 * Manages user accounts, profiles, and person linking
 */
export class UserService {
  private static readonly COLLECTION = 'users';

  /**
   * Get user by ID
   *
   * @param {string} userId - User ID
   * @returns {Promise<User | null>} User object or null if not found
   */
  static async getUserById(userId: string): Promise<User | null> {
    try {
      const userDoc = await getDoc(doc(db, this.COLLECTION, userId));

      if (!userDoc.exists()) {
        return null;
      }

      return {
        uid: userDoc.id,
        ...userDoc.data(),
        createdAt: userDoc.data().createdAt?.toDate(),
        updatedAt: userDoc.data().updatedAt?.toDate(),
        lastLoginAt: userDoc.data().lastLoginAt?.toDate(),
      } as User;
    } catch (error: any) {
      console.error('Error getting user:', error);
      return null;
    }
  }

  /**
   * Update user's linked person
   *
   * @param {string} userId - User ID
   * @param {string | null} personId - Person ID to link, or null to unlink
   * @returns {Promise<void>}
   * @throws {Error} If update fails
   */
  static async updateLinkedPerson(
    userId: string,
    personId: string | null
  ): Promise<void> {
    try {
      await updateDoc(doc(db, this.COLLECTION, userId), {
        personId,
        updatedAt: Timestamp.now(),
      });
    } catch (error: any) {
      console.error('Error updating linked person:', error);
      throw new Error(`Failed to update linked person: ${error.message}`);
    }
  }

  /**
   * Get users linked to a person
   *
   * @param {string} personId - Person ID
   * @returns {Promise<User[]>} Array of users linked to this person
   */
  static async getUsersLinkedToPerson(personId: string): Promise<User[]> {
    try {
      const q = query(
        collection(db, this.COLLECTION),
        where('personId', '==', personId)
      );

      const snapshot = await getDocs(q);

      return snapshot.docs.map(docData => ({
        uid: docData.id,
        ...docData.data(),
        createdAt: docData.data().createdAt?.toDate(),
        updatedAt: docData.data().updatedAt?.toDate(),
        lastLoginAt: docData.data().lastLoginAt?.toDate(),
      })) as User[];
    } catch (error: any) {
      console.error('Error getting users linked to person:', error);
      return [];
    }
  }
}
